import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  birth_date: string;
  birth_time: string | null;
  birth_place: string;
  gender: string | null;
  bio: string | null;
  subscription_status: string;
  is_verified: boolean;
}

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  full_name: string;
  birth_date: string;
  birth_time?: string;
  birth_place: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

const API_URL = import.meta.env.VITE_API_URL || '';

// Очистка legacy-токенов из localStorage (миграция на HttpOnly cookies)
function clearLegacyTokens(): void {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include', // HttpOnly cookies (фаза 1.1)
        cache: 'no-store',
      });

      if (!res.ok) {
        clearLegacyTokens();
        setUser(null);
        return;
      }

      const data = await res.json();
      if (data.success && data.data) {
        setUser({
          id: data.data.id,
          email: data.data.email,
          full_name: data.data.full_name || '',
          birth_date: data.data.birth_date || '',
          birth_time: data.data.birth_time || null,
          birth_place: data.data.birth_place || '',
          gender: data.data.gender || null,
          bio: data.data.bio || null,
          subscription_status: data.data.subscription_status || 'free',
          is_verified: data.data.is_verified ?? true,
        });
      }
    } catch {
      clearLegacyTokens();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email: string, password: string) => {
    let res: Response;
    try {
      res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Получаем HttpOnly cookies
        body: JSON.stringify({ email, password }),
      });
    } catch {
      throw new Error('Сервер недоступен. Проверьте подключение к интернету.');
    }

    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error('Некорректный ответ сервера');
    }

    if (!res.ok) throw new Error(data.message || 'Ошибка входа');

    // Токены больше не сохраняем в localStorage — они в HttpOnly cookies.
    // Очищаем возможные legacy-токены от старой системы.
    clearLegacyTokens();

    await fetchMe();
  };

  const register = async (registerData: RegisterData) => {
    let res: Response;
    try {
      res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...registerData,
          consent_given: true,
        }),
      });
    } catch {
      throw new Error('Сервер недоступен. Проверьте подключение к интернету.');
    }

    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error('Некорректный ответ сервера');
    }

    if (!res.ok) {
      const error = new Error(data.message || 'Ошибка регистрации') as Error & { errors?: any[] };
      if (data.errors) error.errors = data.errors;
      throw error;
    }

    // Don't set user as authenticated yet — email verification required
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Сервер инвалидирует refresh token и очистит cookies
      });
    } catch {
      // Ignore logout errors
    }

    clearLegacyTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        refreshAuth: fetchMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
